import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { fetchReplyTo } from "src/services/CampaignAPI";
import { checkCampaignExists } from "src/services/EngagementAPIs";
import type { AudienceListOption } from "src/utils/audienceListLabels";
import type { CampaignData } from "../../../../../EngagementInterfaces";
import { getUnsubscribedLists } from "../../../../CampaignSetup/CampaignSetupUtils";
import { useAudienceListsAndCompany } from "../../../../CampaignSetup/UseAudienceListsAndCompany";
import { useCampaignSenderDetails } from "./UseCampaignSenderDetails";

const ERROR_KEY = "campaignFlow.aiWizard.errors";

interface InitialBasics {
  sendTo?: string[];
  excludeList?: string[];
  replyTo?: string;
}

/** Audience, sender and campaign-name state shared by the AI and template email wizards. */
export const useCampaignBasics = (
  campaignData: Partial<CampaignData>,
  initial: InitialBasics,
  shouldLoadReplyTo: boolean
) => {
  const { t } = useTranslation();
  const [sendTo, setSendTo] = useState<string[]>(initial.sendTo ?? campaignData.sendTo ?? []);
  const [excludeList, setExcludeList] = useState<string[]>(
    initial.excludeList ?? campaignData.excludeList ?? []
  );
  const [replyTo, setReplyTo] = useState(initial.replyTo ?? "");
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const replyToRequestedRef = useRef(false);

  const handleOptionsLoaded = useCallback(
    (options: AudienceListOption[]) =>
      setExcludeList(current => (current.length ? current : getUnsubscribedLists(options))),
    []
  );
  const { audienceOptions, companyName, isListsLoading } =
    useAudienceListsAndCompany(handleOptionsLoaded);
  const excludeOptions = useMemo(
    () => audienceOptions.filter(option => !sendTo.includes(option.name)),
    [audienceOptions, sendTo]
  );

  useEffect(() => {
    if (!shouldLoadReplyTo || replyToRequestedRef.current) {
      return;
    }
    replyToRequestedRef.current = true;
    fetchReplyTo()
      .then(savedReplyTo => {
        if (savedReplyTo) {
          setReplyTo(current => current || savedReplyTo);
        }
      })
      .catch(() => undefined);
  }, [shouldLoadReplyTo]);

  const updateSendTo = useCallback((next: string[]) => {
    setSendTo(next);
    setExcludeList(current => current.filter(name => !next.includes(name)));
  }, []);

  const clearNameError = useCallback(() => setNameError(null), []);

  const verifyCampaignName = useCallback(
    async (campaignName: string, draftId?: number | null): Promise<boolean> => {
      setIsCheckingName(true);
      try {
        await checkCampaignExists({
          tenant_id: Number(localStorage.getItem("tenant_id") || 0),
          campaign_name: campaignName.trim(),
          ...(typeof draftId === "number" && { draft_id: draftId }),
        });
        setNameError(null);
        return true;
      } catch (error) {
        if (isAxiosError(error) && error.response) {
          setNameError(t(`${ERROR_KEY}.campaignNameExists`));
        } else {
          message.error(t(`${ERROR_KEY}.campaignNameCheckFailed`));
        }
        return false;
      } finally {
        setIsCheckingName(false);
      }
    },
    [t]
  );

  const senderSource = useMemo(
    () => ({
      companyNameEngagements: companyName || campaignData.companyNameEngagements,
      sendTo,
      excludeList,
    }),
    [campaignData.companyNameEngagements, companyName, excludeList, sendTo]
  );
  const defaultSenderDetails = useCampaignSenderDetails(senderSource);
  const senderDetails = useMemo(
    () => ({ ...defaultSenderDetails, replyTo }),
    [defaultSenderDetails, replyTo]
  );

  return {
    sendTo,
    excludeList,
    replyTo,
    nameError,
    isCheckingName,
    audienceOptions,
    excludeOptions,
    companyName,
    isListsLoading,
    senderDetails,
    setSendTo: updateSendTo,
    setExcludeList,
    setReplyTo,
    clearNameError,
    verifyCampaignName,
  };
};
